import { Dispatch, useRef } from 'react'
import { App } from 'antd'
import { useIntl } from 'react-intl'
import * as Sentry from '@sentry/react'

import * as logger from '../../../../services/logger'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { completeNewPasswordChallenge, confirmSigninWithCustomChallengeAnswer, signin, signout } from '@vacationtracker/shared/services/auth'

import { getForceChangePasswordErrorKeys } from '../../../../components/modal-force-change-password/errors'
import { clearRememberedLogin, saveRememberedLogin } from '../../../../util/remembered-login'
import { fetchAuthInfo } from '../auth-info'
import { findInactiveAccount } from '../inactive-account'
import { AUTH_FLOW } from '../../../../services/analytics/auth-events'
import { setSigninEmail, trackLoginStarted, trackSignin } from '../analytics'
import { authInfoErrorToOutcome, authInfoToOutcome, cognitoErrorToOutcome, cognitoResultToOutcome, isMfaCodeMismatch, isStaleLookup } from '../outcomes'
import { LookupOutcome, NewSigninAction, PasswordOutcome, SigninStep } from '../types'
import { ISigninSession } from './use-signin-session'
import { ISigninModals } from './use-signin-modals'

export interface IUseEmailSigninParams {
  step: SigninStep
  localDispatch: Dispatch<NewSigninAction>
  session: ISigninSession
  modals: ISigninModals
}

export interface IEmailSignin {
  submitEmail: (email: string) => Promise<void>
  submitPassword: (password: string) => Promise<void>
  enterMfaCode: (code: string) => Promise<void>
  cancelMfa: () => Promise<void>
  saveNewPassword: (newPassword: string) => Promise<void>
  cancelNewPassword: () => Promise<void>
  emailChanged: (email: string) => void
}

const stepEmail = (step: SigninStep): string => ('email' in step ? (step.email ?? '') : '')

export const useEmailSignin = ({ step, localDispatch, session, modals }: IUseEmailSigninParams): IEmailSignin => {
  const { formatMessage } = useIntl()
  const { notification } = App.useApp()

  // Neither value is rendered, so keeping them out of state avoids re-rendering the
  // whole page on every keystroke and on every lookup.
  const abortControllerRef = useRef<AbortController | null>(null)
  const emailDraftRef = useRef<string>(stepEmail(step))

  const submitEmail = async (email: string) => {
    emailDraftRef.current = email
    localDispatch({ type: 'lookupStarted' })
    setSigninEmail(email)
    trackSignin('SIGN_IN_EMAIL_LOOKUP_SUBMITTED')

    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    let outcome: LookupOutcome
    try {
      outcome = authInfoToOutcome(await fetchAuthInfo(email, controller.signal))
    } catch (err) {
      outcome = authInfoErrorToOutcome(err)
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null
      }
    }

    // The field stays editable during the request, so a late answer must not label an
    // address the visitor has since changed.
    if (isStaleLookup({ requestedEmail: email, currentEmail: emailDraftRef.current })) {
      return
    }

    trackSignin('SIGN_IN_EMAIL_LOOKUP_RESULT', {
      outcome: outcome.kind,
      platform: 'platform' in outcome ? outcome.platform : undefined,
    })
    if (outcome.kind === 'otherPlatform') {
      trackSignin('SIGN_IN_PLATFORM_DETECTED', {
        platform: outcome.platform,
        hasOrganizationName: Boolean(outcome.organizationName),
        source: 'lookup',
      })
    }
    if (outcome.kind === 'unknownError') {
      Sentry.captureException(new Error('signin email lookup failed'), { tags: { flow: AUTH_FLOW } })
    }

    // A recognized address replaces the marker straight away, so a reload carries on with the
    // account being signed in to rather than reverting to the last one that finished. One we
    // cannot sign in to clears it instead: submitting an address is the decision to leave the
    // remembered account, so returning to it on reload would contradict what was just asked
    // for. A malformed address or a failed request say nothing either way, so they leave it.
    if (outcome.kind === 'emailPlatform') {
      saveRememberedLogin({
        email,
        platform: PlatformEnum.Email,
        username: outcome.username,
        adpEnabled: outcome.adpEnabled,
      })
    }
    if (outcome.kind === 'otherPlatform') {
      saveRememberedLogin({ email, platform: outcome.platform as PlatformEnum })
    }
    if (outcome.kind === 'userNotFound' || outcome.kind === 'inactive') {
      clearRememberedLogin()
    }

    localDispatch({ type: 'lookupResolved', outcome, email })
  }

  const submitPassword = async (password: string) => {
    if (step.name !== 'password') return
    const { email, username } = step

    localDispatch({ type: 'passwordSubmitted' })
    trackSignin('SIGN_IN_WITH_EMAIL_BUTTON_CLICKED', { platform: 'email' })
    trackLoginStarted('email')

    let outcome: PasswordOutcome
    try {
      outcome = cognitoResultToOutcome(await signin(username, password))
    } catch (err) {
      outcome = cognitoErrorToOutcome(err)
      if (outcome.kind === 'unknownError') {
        Sentry.captureException(err, { tags: { flow: AUTH_FLOW, reason: 'email_signin' } })
      }
    }

    // Deactivating an account removes it from Cognito, which then turns every password down as
    // incorrect. Asked again only then, so an account deactivated since the address step is told so.
    if (outcome.kind === 'incorrectPassword') {
      const inactive = await findInactiveAccount(email, PlatformEnum.Email)
      if (inactive) {
        trackSignin('SIGN_IN_ACCOUNT_INACTIVE', { platform: PlatformEnum.Email, detectedBy: inactive.detectedBy })
        localDispatch({ type: 'inactiveAccountDetected', email, organizationName: inactive.organizationName })
        return
      }
    }

    localDispatch({ type: 'passwordResolved', outcome })

    // Listing what carries on rather than what fails, so an outcome added later is reported
    // instead of going untracked.
    if (!['signedIn', 'mfaRequired', 'newPasswordRequired'].includes(outcome.kind)) {
      trackSignin('SIGN_IN_WITH_EMAIL_ERROR', { outcome: outcome.kind, platform: 'email' })
    }

    // The username came from a lookup that may be old enough to have gone stale. Forget it so
    // the next visit starts from the address again instead of repeating an attempt that
    // cannot work.
    if (outcome.kind === 'userNotFound') {
      clearRememberedLogin()
    }

    // The reducer has already moved the page to the code step; nothing further to open.
    if (outcome.kind === 'mfaRequired') {
      return
    }

    if (outcome.kind === 'newPasswordRequired') {
      if (outcome.missingAttributes.length > 0) {
        await reportMissingAttributes(outcome.missingAttributes)
        return
      }
      modals.showForceChangePassword(true)
      return
    }

    if (outcome.kind === 'signedIn') {
      await session.completeLogin({
        username: outcome.username,
        email,
        platform: PlatformEnum.Email,
        analyticsPayload: { platform: 'email' },
      })
    }
  }

  const reportMissingAttributes = async (missingAttributes: string[]) => {
    localDispatch({
      type: 'bannerRaised',
      banner: { kind: 'missingAttributes', attributes: missingAttributes.join(', ') },
    })
    Sentry.captureException(new Error('FORCE_CHANGE_PASSWORD missing attributes'), {
      tags: { flow: AUTH_FLOW, reason: 'missing_attributes' },
      extra: { missingAttributes },
    })
    try {
      await signout()
    } catch (signoutError) {
      logger.debug('signout after missing attributes failed', signoutError)
    }
  }

  const enterMfaCode = async (code: string) => {
    localDispatch({ type: 'mfaSubmitted' })
    try {
      const cognitoResponse = await confirmSigninWithCustomChallengeAnswer(code)
      await session.completeLogin({
        username: cognitoResponse.username,
        email: stepEmail(step),
        platform: PlatformEnum.Email,
        analyticsPayload: { platform: 'email' },
      })
    } catch (e) {
      // Said on the step itself rather than in a corner toast: the answer to a rejected code
      // is to retype it, which happens right under the message.
      localDispatch({ type: 'mfaCodeRejected', isMismatch: isMfaCodeMismatch(e) })
      Sentry.captureException(e)
    }
  }

  /**
   * Cognito is holding a half-finished sign-in open, and Amplify keeps it in memory rather
   * than in the step, so walking away has to end it explicitly or the next attempt resumes
   * the abandoned challenge.
   */
  const cancelMfa = async () => {
    localDispatch({ type: 'restarted' })
    try {
      await signout()
    } catch (signoutError) {
      logger.debug('signout after mfa cancel failed', signoutError)
    }
  }

  const saveNewPassword = async (newPassword: string) => {
    modals.setForceChangePasswordLoading(true)
    try {
      const cognitoResponse = await completeNewPasswordChallenge(newPassword)
      modals.showForceChangePassword(false)
      modals.setForceChangePasswordLoading(false)
      notification.success({
        message: formatMessage({ id: 'modal.forceChangePassword.successNotification' }),
        description: formatMessage({ id: 'modal.forceChangePassword.successNotificationMessage' }),
        duration: 10,
      })
      await session.completeLogin({
        username: cognitoResponse.username,
        email: stepEmail(step),
        platform: PlatformEnum.Email,
        analyticsPayload: { platform: 'email' },
      })
    } catch (err) {
      modals.setForceChangePasswordLoading(false)
      const { titleKey, descriptionKey } = getForceChangePasswordErrorKeys(err.name as string)
      notification.error({
        message: formatMessage({ id: titleKey }),
        description: formatMessage({ id: descriptionKey }),
        key: 'FORCE_CHANGE_PASSWORD_ERROR',
        duration: 10,
      })
      Sentry.captureException(err, {
        tags: { flow: AUTH_FLOW, reason: 'force_change_password' },
        extra: { exceptionName: err?.name },
      })
    }
  }

  const cancelNewPassword = async () => {
    modals.showForceChangePassword(false)
    modals.setForceChangePasswordLoading(false)
    localDispatch({ type: 'busyChanged', isBusy: false })
    try {
      await signout()
    } catch (signoutError) {
      logger.debug('signout after force-change-password cancel failed', signoutError)
    }
  }

  const emailChanged = (email: string) => {
    emailDraftRef.current = email
    localDispatch({ type: 'bannerCleared' })
  }

  return { submitEmail, submitPassword, enterMfaCode, cancelMfa, saveNewPassword, cancelNewPassword, emailChanged }
}
